import { getFormattedNumber } from '@src/slide/domain/ui-model/element-modeles/property/chart/util/format';
import { LabelTextRunFieldType } from '@src/slide/domain/ui-model/element-modeles/constant/chart';
import { ChartTextProperty } from '@src/slide/domain/ui-model/element-modeles/property/chart/chart-shape-property';
import type {
    IBaseDataLabelProperty,
    IDataLabelProperty,
    IDataLabelsProperty,
} from '@src/slide/domain/ui-model/element-modeles/property/chart/sub-chart/data-label/data-label.interface';
import type {
    IBaseDataLabel,
    IDataLabel,
    IDataLabels,
} from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/sub-chart/sub-chart-properties.interface';
import { ISeries, ISeriesProperty } from '../series/series.interface';
import { IDataItemProperty } from '../data-item/data-item.interface';
import { IChartActionType } from '../../constant';
import type {
    IParagraph,
} from '@src/slide/domain/ui-model/element-modeles/property/text/interface/editor-para.interface';
import { transaction } from '@src/slide/domain/slide-app';
import { IRect } from '@src/slide/infrastructure/module/independent/graphic/rect/rect';
import {
    IChartExt,
    IChartExtension,
    IChartManualLayout,
} from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/chart-properties.interface';

export const DEFAULT_LABEL_SEPARATOR = ', ';

class BaseDataLabelProperty<T extends IBaseDataLabel> extends ChartTextProperty<T>
    implements IBaseDataLabelProperty<T> {
    public updateProps(props: Partial<T>): void {}
}

export class DataLabelProperty extends BaseDataLabelProperty<IDataLabel> implements IDataLabelProperty {
    public content: string;
    public dataItem: IDataItemProperty;
    public formatCodeColor?: string;
    private rawParaList;

    public updateProps(props: Partial<IDataLabel>) {
        this.dataItem.updateDataLabel(props);
    }

    public get layout() {
        const { layout } = this.uiData;
        const extLayout = this.getExtLayout();
        const hasLayout = layout || extLayout;

        if (hasLayout) {
            return {
                ...extLayout,
                ...layout,
            };
        }
    }

    /**
     * 更新数据标签的layout偏移
     * offset参数是与当前位置的偏移，如果已经有Layout，会需要做offset合并
     */
    // eslint-disable-next-line complexity
    public updateLayout(rect: Partial<IRect>) {
        const layout = { ...this.uiData.layout };

        if (rect.left !== undefined) {
            layout.left = (layout.left || 0) + (rect.left || 0);
        }
        if (rect.top !== undefined) {
            layout.top = (layout.top || 0) + (rect.top || 0);
        }

        let extLayout;
        if (rect.height !== undefined) {
            // 如果是layout中的数据，就更新layout，否则更新extLayout
            if (layout.height !== undefined) {
                layout.height = rect.height;
            } else {
                extLayout = { ...extLayout, height: rect.height };
            }
        }
        if (rect.width !== undefined) {
            if (layout.width !== undefined) {
                layout.width = rect.width;
            } else {
                extLayout = { ...extLayout, width: rect.width };
            }
        }

        this.updateProps({ layout, ext: extLayout ? this.createExtPatch(extLayout) : undefined });
    }

    public init(dataItemProperty: IDataItemProperty) {
        this.dataItem = dataItemProperty;
        this.generateLabelContent(dataItemProperty);

        return this;
    }

    public getParagraphList(opts?): IParagraph[] {
        if (this.rawParaList) {
            const { paragraphList: defaultParaList } = this;
            const shouldMerge = this.shouldMergeDefaultTextProps();
            return (
                this.rawParaList?.map(paragraph => ({
                    ...paragraph,
                    paragraphProps: {
                        textAlign: 'center',  // 数据标签默认居中
                        ...paragraph?.paragraphProps,
                    },
                    textRuns: paragraph.textRuns?.map(run => ({
                        ...run,
                        styles: {
                            ...(shouldMerge ? defaultParaList?.[0]?.paragraphProps?.defaultRunProps : { }),
                            ...paragraph.paragraphProps?.defaultRunProps,
                            ...run.styles,
                        },
                    })),
                })) || []
            );
        }
        return super.getParagraphList(opts ?? {
            textAlign: 'center',
            content: this.content,
        });
    }

    // eslint-disable-next-line complexity
    protected generateLabelContent(dataItem: IDataItemProperty) {
        const { value, valueFormatCode, category, ser,
            // @ts-ignore TODO 这个bubble看怎么处理
            originSize: bubbleSize } = dataItem;
        const {
            showVal,
            showCategoryName,
            showPercent,
            showSeriesName,
            showBubbleSize,
            separator = DEFAULT_LABEL_SEPARATOR,
            text,
            formatCode,
        } = this.uiData;

        let content = '';
        let percent = '';
        const children: Array<string | number> = [];
        const categoryName = this.getFormattedCategoryValueText(dataItem);
        const formattedValueText = this.getFormattedValueText(dataItem);

        if (showPercent) {
            const sumValue = ser?.dataList
                .map(data => data.valuePoint?.value || 0)
                .reduce((pre, cur) => Number(pre) + Number(cur || 0));
            const percentData = value / (sumValue as number);

            if (formatCode && formatCode !== 'General') {
                percent = getFormattedNumber(percentData, formatCode);
            } else {
                percent = `${
                    // @ts-ignore
                    Math.round(percentData * 100)
                }%`;
            }
        }

        const displayValue = showVal ? formattedValueText : '';

        // 自定义标签内容（修改了标签内容，不只是单纯的选项拼接）
        if (text) {
            const paragraphList = text.paragraphList || [];
            const containedFieldTypeList: LabelTextRunFieldType[] = [];

            const texts: string[] = [];
            const count = paragraphList.length;
            this.rawParaList = paragraphList.map((p, paraIndex) => {
                // eslint-disable-next-line complexity
                const rawTextRuns = p.textRuns.map((run) => {
                    let textContent = '';
                    const { text, type, fieldType } = run;
                    if (type === 'run') textContent = text;
                    // 一个fieldRun因为包含符号(e.g: [系列名称])会被拆成三个run，而其实每个fieldType最多一个。所以这里在循环的时候，过滤相同的filedRun
                    if (type === 'field' && !containedFieldTypeList.includes(fieldType)) {
                        containedFieldTypeList.push(fieldType);
                        switch (fieldType) {
                            case LabelTextRunFieldType.CATEGORY_NAME:
                            case LabelTextRunFieldType.X_VALUE:
                                textContent = `${categoryName}`;
                                break;
                            case LabelTextRunFieldType.VALUE:
                            case LabelTextRunFieldType.Y_VALUE:
                                textContent = displayValue;
                                break;
                            case LabelTextRunFieldType.SERIES_NAME:
                                textContent = ser.name;
                                break;
                            case LabelTextRunFieldType.BUBBLE_SIZE:
                                textContent = bubbleSize || 0;
                                break;
                            case LabelTextRunFieldType.PERCENTAGE:
                                textContent = percent;
                                break;
                            case LabelTextRunFieldType.CELL_RANGE:
                                // CELL_RANGE的情况，在ooxml里在extList里面，目前拿不到。降级为格式化的value做兜底显示
                                textContent = formattedValueText;
                                break;
                            default:
                                textContent = text;
                                break;
                        }
                    }
                    texts.push(`${textContent}`);
                    return { ...run, text: `${textContent}` };
                }).filter(run => !!run.text);
                if (count > 1 && paraIndex !== count - 1) texts.push('\n');
                return { ...p, textRuns: rawTextRuns };
            });
            content =  texts.join('');
        } else {
            // 从上到下有顺序
            showSeriesName && children.push(ser?.name);
            showCategoryName && children.push(categoryName);
            showVal && children.push(displayValue);
            showBubbleSize && children.push(bubbleSize || 0);
            showPercent && children.push(percent);

            content = children.join(separator);
        }

        this.content = content;
    }

    private getFormattedValueText(dataItem: IDataItemProperty) {
        const { value, valueFormatCode } = dataItem;
        const { formatCode } = this.uiData;

        let valueText = '';
        // 有些情况，存在有label.numFmt为General，依然以valueFormatCode为准
        const actualUsedFormatCode = formatCode === 'General' && valueFormatCode ? valueFormatCode : formatCode || valueFormatCode;

        // 如果是字符串并且不是数字，就显示为0
        if (typeof value === 'string' && isNaN(Number(value))) {
            valueText = '0';
        } else {
            valueText = getFormattedNumber(value, actualUsedFormatCode) as string;

            // formatCode应该是一个可以穷举的模板，有一部分模板可以添加颜色，而这这个颜色只会应用于负数，相关参考：
            // https://c-rex.net/projects/samples/ooxml/e1/Part4/OOXML_P4_DOCX_numFmt_topic_ID0EHDH6.html#topic_ID0EHDH6
            const formatCodeColorMatchResult = actualUsedFormatCode?.match(/;\[[a-zA-Z]+\]/gi);
            if (value < 0 && formatCodeColorMatchResult && formatCodeColorMatchResult.length > 0) {
                // 匹配出中间的颜色，e,g: ;[red]
                this.formatCodeColor = formatCodeColorMatchResult[0].slice(2, -1).toLowerCase();
            }
        }

        return valueText;
    }

    private getFormattedCategoryValueText(dataItem: IDataItemProperty) {
        const { category = '', categoryFormatCode } = dataItem;

        return getFormattedNumber(category, categoryFormatCode) as string;
    }

    /**
     * 自定义文本，是否要merge 默认文本样式。
     * 参照office，正常情况不merge，除非满足以下两个条件：
     * 1. dataLabel对象不存在默认的paragraphList
     * 2. dataLabel对象不存在默认的shape props
     * @private
     */
    private shouldMergeDefaultTextProps(): boolean {
        const { text: richText } = this;
        if (richText) {
            const orgDataLabel = this.dataItem.dataLabel;
            if (!orgDataLabel?.paragraphList && !orgDataLabel?.props) {
                return true;
            }
        }
        return false;
    }

    private getExtLayout() {
        const { ext } = this.uiData;
        let extLayout;
        ext?.forEach(item => item.egExtension?.forEach((item) => {
            if (item.layout) {
                extLayout = item.layout;
            }
        }));

        return extLayout;
    }

    private createExtPatch(uiExtLayout: IChartManualLayout): IChartExt[] {
        // 这里要拿原始的dataLabel，而不是拿Merge之后的labelProperty
        const { ext = [] } = this.dataItem.dataLabel || {};

        // 找到第一个带有 layout 属性的 egExtension 索引
        let layoutExtIndex = -1;
        let layoutEgExtIndex = -1;

        ext.some((extItem, extIndex) =>
            extItem.egExtension?.some((egExtItem, egExtIndex) => {
                if (egExtItem.layout) {
                    layoutExtIndex = extIndex;
                    layoutEgExtIndex = egExtIndex;
                    return true;
                }
                return false;
            }));

        if (layoutEgExtIndex === -1) {
            // 如果没有找到带有 layout 属性的项，直接在 ext 列表末尾插入新项
            return [...ext, { egExtension: [{ layout: uiExtLayout }] }];
        }

        // 构造 egExtension patch
        const egExtPatch: IChartExtension[] = [...new Array(layoutEgExtIndex), { layout: uiExtLayout }];

        // 构造 ext patch
        const extPatch: IChartExt[] = [...new Array(layoutExtIndex), { egExtension: egExtPatch }];

        return extPatch;
    }
}

export class DataLabelsProperty extends BaseDataLabelProperty<IDataLabels> implements IDataLabelsProperty {
    public seriesProperty: ISeriesProperty;

    public init(seriesProperty: ISeriesProperty) {
        this.seriesProperty = seriesProperty;
        return this;
    }

    public updateProps(props: Partial<IDataLabels>): void {
        this.seriesProperty.updateDataLabels(props);
    }

    public updateFontStyle(actionType: IChartActionType, value) {
        transaction(() => {
            // 更新series datalables text
            super.updateFontStyle(actionType, value);

            this.updateDataListLabelFontStyle(actionType, value);
        });
    }

    public updateParagraphList(paragraphList: IParagraph[]): void {
        this.seriesProperty.updateDataLabels({ paragraphList }, false);
    }

    // 更新series datalist databel text
    private updateDataListLabelFontStyle(actionType: IChartActionType, value) {
        const subChartModel = this.context.getSubChartModel();
        const dataItems = subChartModel.series.find(ser => ser.idx === this.seriesProperty.idx)?.dataList;
        const updateDataList = dataItems?.map((dataItem) => {
            if (dataItem.dataLabel) {
                const dataItemProperty = subChartModel.getSubChartProperty().data
                    .find(data => data.id === dataItem.id);
                if (dataItemProperty?.label) {
                    return {
                        dataLabel: dataItemProperty.label.getUpdatedTextProps(actionType, value),
                    };
                }
            }
            return undefined;
        });
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        this.seriesProperty.updateProps({ dataList: updateDataList } as ISeries);
    }
}
